import {
  TabPanel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanelProps
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import React from "react";
import { animated, useSpring } from "react-spring";
import { Subheading } from "../components/Subheading";
import theme from "../theme";
import { mediaQuery } from "../theme/breakpoints";

enum GalleryTabs {
  PRODUCTION,
  HEADSHOTS
}

const TabWrapper = styled.div`
  button {
    cursor: pointer;
    // Greyed out when not selected
    color: #999;
    background-color: ${theme.colors.palette[200]};
    transition: all 0.3s;

    &:hover,
    &:active,
    &:focus,
    &[aria-selected="true"] {
      color: ${theme.colors.black};
      background-color: ${theme.colors.palette[100]};
      border-color: ${theme.colors.palette[100]};
    }
  }

  h3 {
    font-size: ${theme.fontSizes.xl};
  }

  ${mediaQuery(theme.breakpoints.sm)} {
    h3 {
      font-size: ${theme.fontSizes["2xl"]};
    }
  }
`;

interface GalleryTabProps {
  tab: GalleryTabs;
  chosenTab: GalleryTabs;
  children: React.ReactNode;
}

const TabHeader = React.forwardRef<HTMLButtonElement, GalleryTabProps>(
  function GalleryTab(props: GalleryTabProps, ref) {
    return (
      <TabWrapper>
        <Tab ref={ref} {...props} isSelected={props.tab === props.chosenTab}>
          <Subheading padding={theme.space[0]}>{props.children}</Subheading>
        </Tab>
      </TabWrapper>
    );
  }
);

const AnimatedTabPanel = (
  props: TabPanelProps & { visible: boolean; onAnimFinished: () => void }
) => {
  const { onAnimFinished, visible, ...rest } = props;

  const animProps = useSpring({
    to: { opacity: visible ? 1 : 0 },
    config: {
      duration: theme.animation.speed.normalMs
    },
    onRest: () => {
      onAnimFinished();
    }
  });

  return (
    <animated.div style={animProps}>
      <TabPanel {...rest}>{props.children}</TabPanel>
    </animated.div>
  );
};

interface TabsProps {
  tabTitles: string[];
  tabContents: React.ReactNode[];
}

const AnimatingTabs: React.FC<TabsProps> = (props) => {
  if (props.tabTitles.length !== props.tabContents.length) {
    throw new Error("Must invoke Tabs with same number of titles and contents");
  }

  // Start with first tab open
  const [chosenTab, setChosenTab] = React.useState<number>(0);
  const [nextChosenTab, setNextChosenTab] = React.useState<number>(0);
  const [contentVisible, setContentVisible] = React.useState<boolean>(true);

  // "Initiate" a tab switch by setting next tab, and
  // hiding content
  const updateTab = (nextTab: GalleryTabs) => {
    if (chosenTab === nextTab) {
      return;
    }
    setNextChosenTab(nextTab);
    setContentVisible(false);
  };

  // When hiding animation finishes, now _actually_ change
  // tab, and show content
  const animationEndCallback = () => {
    setChosenTab(nextChosenTab);
    setContentVisible(true);
  };

  return (
    <Tabs
      // isFitted={true}
      index={chosenTab}
      onChange={(next) => updateTab(next)}
      variant={"line"}
    >
      <TabList
        style={{ borderBottom: `6px solid ${theme.colors.palette[100]}` }}
      >
        {props.tabTitles.map((tt, idx) => (
          <TabHeader
            key={`animating-tabs-title-${idx}`}
            tab={idx}
            chosenTab={nextChosenTab}
          >
            {tt}
          </TabHeader>
        ))}
      </TabList>
      <TabPanels borderTopColor={theme.colors.palette[100]}>
        {props.tabContents.map((tc, idx) => (
          <AnimatedTabPanel
            visible={contentVisible}
            onAnimFinished={animationEndCallback}
            key={`animating-tabs-panel-${idx}`}
            padding={0}
            borderColor={theme.colors.palette[100]}
          >
            {tc}
          </AnimatedTabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};

export default AnimatingTabs;
